import React, {forwardRef, useEffect, useImperativeHandle, useState} from 'react';
import TextField from '@material-ui/core/TextField/TextField';
import { Grid, makeStyles } from '@material-ui/core';
import _ from 'lodash';
import { useTranslation } from "react-i18next";


const useStyles = makeStyles((theme) => ({
    Grid: {
        [theme.breakpoints.up('sm')]: {
            paddingRight: theme.spacing(2),
        },
    },
    MuiFormControlRoot: {
        marginTop: 0,
    },
    outlinedInput: {
        padding: 10,
        fontSize: 15,
        [theme.breakpoints.down('xs')]: {
            padding: 19,
        },
    },
    outlinedLabel: {
        fontSize: 15,
    },
}));


const RequestOfferForm = forwardRef((props, ref) => {

    const { t } = useTranslation(["receiveOfferForm"]);
    const classes = useStyles();
    const { } = props;


    const initForm = {
        email: {
            value: '',
            error: null,
        },
        name: {
            value: '',
            error: null,
        },
        company: {
            value: '',
            error: null,
        },
        phonenumber: {
            value: '',
            error: null,
        },
        zip: {
            value: '',
            error: null,
        },
        city: {
            value: '',
            error: null,
        },
        street: {
            value: '',
            error: null,
        },
        country: {
            value: '',
            error: null,
        }
    };

    const [form, setForm] = useState(initForm);

    const getErrorMessage = error => {
        return error ? t(error) : null;
    };

    const validateTextInput = (name, value) => {
        const emailRegexp = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;

        if (value === '') {
            return 'errors.required';
        }

        if (name === 'email' && !emailRegexp.test(value)) {
            return 'errors.invalidEmail';
        }

        return null;
    };

    const handleChange = (event) => {
        const { name, value } = event.target;

        const formTemp = _.cloneDeep(form);
        formTemp[name].value = value;
        setForm(formTemp);
    }

    const handleBlur = (event) => {
        const { name, value } = event.target;

        const formTemp = _.cloneDeep(form);
        formTemp[name].error = validateTextInput(name, value);
        setForm(formTemp);
    }

    const validate = () =>{
        let hasError = false;
        const values = {};
        const formTemp = _.cloneDeep(form);

        const inputFields = ['email', 'name', 'company', 'phonenumber', 'zip', 'city', 'street', 'country'];

        for (const inputField of inputFields) {
            values[inputField] = form[inputField].value;

            const error = validateTextInput(inputField, formTemp[inputField].value);

            if (error !== null) {
                hasError = true;
            }

            formTemp[inputField].error = error;
        }

        setForm(formTemp);

        return { hasError, ...values };
    }

    useImperativeHandle(ref, () => ({
        validateForm() {
            return validate();
        }
    }));

    return (
        <div>
            <TextField
                name="email"
                value={form.email.value}
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('email')}
                margin="dense"
                variant="outlined"
                fullWidth
                error={!!form.email.error}
                helperText={getErrorMessage(form.email.error)}
                InputProps={{
                    classes: {
                        input: classes.outlinedInput,
                    },
                }}
                InputLabelProps={{
                    classes: {
                        outlined: classes.outlinedLabel,
                    },
                }}
                classes={{
                    root: classes.MuiFormControlRoot,
                }}
            />
            <TextField
                name="name"
                value={form.name.value}
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('name')}
                margin="dense"
                variant="outlined"
                fullWidth
                error={!!form.name.error}
                helperText={getErrorMessage(form.name.error)}
                InputProps={{
                    classes: {
                        input: classes.outlinedInput,
                    },
                }}
                InputLabelProps={{
                    classes: {
                        outlined: classes.outlinedLabel,
                    },
                }}
            />
            <TextField
                name="company"
                value={form.company.value}
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('company')}
                margin="dense"
                variant="outlined"
                fullWidth
                error={!!form.company.error}
                helperText={getErrorMessage(form.company.error)}
                InputProps={{
                    classes: {
                        input: classes.outlinedInput,
                    },
                }}
                InputLabelProps={{
                    classes: {
                        outlined: classes.outlinedLabel,
                    },
                }}
            />
            <TextField
                name="phonenumber"
                value={form.phonenumber.value}
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('phonenumber')}
                margin="dense"
                variant="outlined"
                fullWidth
                error={!!form.phonenumber.error}
                helperText={getErrorMessage(form.phonenumber.error)}
                InputProps={{
                    classes: {
                        input: classes.outlinedInput,
                    },
                }}
                InputLabelProps={{
                    classes: {
                        outlined: classes.outlinedLabel,
                    },
                }}
            />
            <Grid container>
                <Grid item xs={12} sm={4} className={classes.Grid}>
                    <TextField
                        name="zip"
                        value={form.zip.value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label={t('zip')}
                        margin="dense"
                        variant="outlined"
                        fullWidth
                        error={!!form.zip.error}
                        helperText={getErrorMessage(form.zip.error)}
                        InputProps={{
                            classes: {
                                input: classes.outlinedInput,
                            },
                        }}
                        InputLabelProps={{
                            classes: {
                                outlined: classes.outlinedLabel,
                            },
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={8}>
                    <TextField
                        name="city"
                        value={form.city.value}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label={t('city')}
                        margin="dense"
                        variant="outlined"
                        fullWidth
                        error={!!form.city.error}
                        helperText={getErrorMessage(form.city.error)}
                        InputProps={{
                            classes: {
                                input: classes.outlinedInput,
                            },
                        }}
                        InputLabelProps={{
                            classes: {
                                outlined: classes.outlinedLabel,
                            },
                        }}
                    />
                </Grid>
            </Grid>
            <TextField
                name="street"
                value={form.street.value}
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('street')}
                margin="dense"
                variant="outlined"
                fullWidth
                error={!!form.street.error}
                helperText={getErrorMessage(form.street.error)}
                InputProps={{
                    classes: {
                        input: classes.outlinedInput,
                    },
                }}
                InputLabelProps={{
                    classes: {
                        outlined: classes.outlinedLabel,
                    },
                }}
            />
            <TextField
                name="country"
                value={form.country.value}
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('country')}
                margin="dense"
                variant="outlined"
                fullWidth
                error={!!form.country.error}
                helperText={getErrorMessage(form.country.error)}
                InputProps={{
                    classes: {
                        input: classes.outlinedInput,
                    },
                }}
                InputLabelProps={{
                    classes: {
                        outlined: classes.outlinedLabel,
                    },
                }}
            />
        </div>
    );
});

export default RequestOfferForm;
